import { BackgroundColorsMap, GradientsColorsMap, MilanBackgroundColors, Typography } from '@milanlaser/mlcomponents';
import { Store } from 'clinics/clinics';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import React, { useMemo } from 'react';
import { useEffect, useRef } from 'react';
import { useReviewsIoScript } from '../../../../hooks/useReviewsIoScript';
import { initializeReviewTabSwitcher } from '../../../../utils/autoTabSwitcher';
import { ReviewsData } from '../../../../utils/ssr/types';

declare global {
    interface Window {
        ReviewsWidget: {
            new (id: string, config: unknown): unknown;
        };
    }
}

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S37ReviewsOverallProps = BuilderProps<{
    store: Store;
    reviewsData?: ReviewsData;
}> & {
    title: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors;
    backgroundGradient?: MilanBackgroundColors;
};

export function S37ReviewsOverall(props: S37ReviewsOverallProps) {
    const {
        title,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        backgroundGradient,
        ...restProps
    } = props;

    const {
        builderState: {
            state: { store, reviewsData },
        },
    } = restProps;
    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;
    const backgroundStyle = (backgroundColor && BackgroundColorsMap[backgroundColor]) || 'None';
    const backgroundGradientStyle =
        backgroundGradient && backgroundGradient in GradientsColorsMap
            ? GradientsColorsMap[backgroundGradient as keyof typeof GradientsColorsMap]
            : 'None';

    const reviews = reviewsData
        ? {
              value: reviewsData.average || 0,
              reviewCount: reviewsData.total || 0,
          }
        : undefined;

    const renderReviews = reviews !== undefined && reviews.reviewCount > 0;
    const tabSwitcherCleanupRef = useRef<(() => void) | null>(null);
    const { isLoaded: scriptLoaded, isError: scriptError } = useReviewsIoScript();
    const widgetInitialized = useRef(false);

    // Don't render at all if env variable is not set to true
    if (!process.env.GATSBY_SHOW_REVIEWS_IO || process.env.GATSBY_SHOW_REVIEWS_IO !== 'true') {
        return null;
    }

    const reviewsConfig = useMemo(() => {
        return {
            //Your REVIEWS.io Store ID and widget type:
            store: 'milanlaser.com',
            widget: 'polaris',

            options: {
                types: 'store_review,product_review,third_party_review',
                enable_sentiment_analysis: true,
                lang: 'en',
                //Possible layout options: bordered, large and reverse.
                layout: '',
                //How many reviews & questions to show per page?
                per_page: 3,
                store_review: {
                    hide_if_no_results: false,
                },
                third_party_review: {
                    hide_if_no_results: false,
                },
                //Product specific settings. Provide product SKU for which reviews should be displayed:
                product_review: {
                    //Display product reviews - include multiple product SKUs seperated by Semi-Colons (Main Indentifer in your product catalog )
                    sku: store.salesforceValue,
                    hide_if_no_results: false,
                },
                //Questions settings:
                questions: {
                    hide_if_no_results: false,
                    enable_ask_question: true,
                    enable_ask_question_button_style: false, //Show "be the first to ask a question" text as a button
                    show_dates: true,
                    //Display group questions by providing a grouping variable, new questions will be assigned to this group.
                    grouping: '[Group questions by providing a grouping variable here or a specific product SKU]',
                },
                //Header settings:
                header: {
                    enable_summary: true, //Show overall rating & review count
                    enable_ratings: true,
                    enable_attributes: false,
                    enable_image_gallery: true, //Show photo & video gallery
                    enable_percent_recommended: false, //Show what percentage of reviewers recommend it
                    enable_write_review: false, //Show "Write Review" button
                    enable_ask_question: false, //Show "Ask Question" button
                    enable_sub_header: true, //Show subheader
                    rating_decimal_places: 2,
                    use_write_review_button: false, //Show "be the first to leave a review" text as a button
                    enable_if_no_results: false, //Show header when there are no results
                },

                //AI summary settings
                sentiment: {
                    badge_text: 'Our Customers Say',
                },

                //Filtering settings:
                filtering: {
                    enable: true, //Show filtering options
                    enable_text_search: false, //Show search field
                    enable_sorting: true, //Show sorting options (most recent, most popular)
                    enable_product_filter: false, //Show product options filter
                    enable_media_filter: true, //Show reviews with images/video/media options
                    enable_overall_rating_filter: true, //Show overall rating breakdown filter
                    enable_language_filter: false, // Filter by review language
                    enable_language_filter_language_change: false, // Update widget language based on language selected
                    enable_ratings_filters: true, //Show product attributes filter
                    enable_attributes_filters: true, //Show author attributes filter
                    enable_expanded_filters: false, //Show filters as a section instead of dropdown
                },

                //Review settings:
                reviews: {
                    enable_avatar: true, //Show author avatar
                    enable_reviewer_name: true, //Show author name
                    enable_reviewer_address: true, //Show author location
                    reviewer_address_format: 'city, country', //Author location display format
                    enable_verified_badge: true, //Show "Verified Customer" badge
                    enable_subscriber_badge: false, //Show "Verified Subscriber" badge
                    review_content_filter: 'undefined', //Filter content
                    enable_reviewer_recommends: true, //Show "I recommend it" badge
                    enable_attributes: true, //Show author attributes
                    enable_product_name: true, //Show display product name
                    enable_review_title: true, //Show review title
                    enable_replies: true, //Show review replies
                    enable_images: true, //Show display review photos
                    enable_ratings: true, //Show product attributes (additional ratings)
                    enable_share: true, //Show share buttons
                    enable_helpful_vote: true, //Show "was this helpful?" section
                    enable_helpful_display: true, //Show how many times times review upvoted
                    enable_report: true, //Show report button
                    enable_date: true, //Show when review was published
                    enable_third_party_source: true, //Show third party source
                    enable_duplicate_reviews: undefined, //Show multiple reviews from the same user
                },
            },
            //Translation settings
            translations: {
                'Verified Customer': 'Verified Customer',
            },
            //Style settings:
            styles: {
                //Base font size is a reference size for all text elements. When base value gets changed, all TextHeading and TexBody elements get proportionally adjusted.
                '--base-font-size': '16px',

                //Button styles (shared between buttons):
                '--common-button-font-family': 'proxima-nova, sans-serif',
                '--common-button-font-size': '18px',
                '--common-button-font-weight': '500',
                '--common-button-letter-spacing': '0',
                '--common-button-text-transform': 'none',
                '--common-button-vertical-padding': '14px',
                '--common-button-horizontal-padding': '20px',
                '--common-button-border-width': '2px',
                '--common-button-border-radius': '30px',

                //Primary button styles:
                '--primary-button-bg-color': '#0088AB',
                '--primary-button-border-color': '#FFFFFF00',
                '--primary-button-text-color': '#ffffff',

                //Secondary button styles:
                '--secondary-button-bg-color': '#003542',
                '--secondary-button-border-color': '#FFFFFF00',
                '--secondary-button-text-color': '#FFFFFF',

                //Star styles:
                '--common-star-color': '#F4D500',
                '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
                '--medium-star-size': '22px',
                '--small-star-size': '19px',

                //Heading styles:
                '--heading-text-color': '#00A5B4',
                '--heading-text-font-weight': '600',
                '--heading-text-font-family': 'greycliff-cf, sans-serif',
                '--heading-text-line-height': '1.4',
                '--heading-text-letter-spacing': '0',
                '--heading-text-transform': 'none',

                //Body text styles:
                '--body-text-color': '#003542',
                '--body-text-font-weight': '400',
                '--body-text-font-family': 'proxima-nova, sans-serif',
                '--body-text-line-height': '1.4',
                '--body-text-letter-spacing': '0',
                '--body-text-transform': 'none',

                //Input field styles:
                '--inputfield-text-font-family': 'proxima-nova, sans-serif',
                '--input-text-font-size': '18px',
                '--inputfield-text-font-weight': '400',
                '--inputfield-text-color': '#003542',
                '--inputfield-border-color': 'rgba(0,0,0,0.2)',
                '--inputfield-background-color': '#FFFFFF',
                '--inputfield-border-width': '1px',
                '--inputfield-border-radius': '30px',

                '--common-border-color': '#90D5D5',
                '--common-border-width': '1px',
                '--common-sidebar-width': '190px',

                //Filters panel styles:
                '--filters-panel-bg-color': 'transparent',
                '--filters-panel-font-size': '16px',
                '--filters-panel-text-color': '16px',
                '--filters-panel-horizontal-padding': '0',
                '--filters-panel-vertical-padding': '0',

                //Slider indicator (for attributes) styles:
                '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
                '--slider-indicator-button-color': '#0E1311',
                '--slider-indicator-width': '190px',

                //Badge styles:
                '--badge-icon-color': '#0E1311',
                '--badge-icon-font-size': 'inherit',
                '--badge-text-color': '#0E1311',
                '--badge-text-font-size': 'inherit',
                '--badge-text-letter-spacing': 'inherit',
                '--badge-text-transform': 'inherit',

                //Author styles:
                '--author-font-size': 'inherit',
                '--author-text-transform': 'none',

                //Author avatar styles:
                '--avatar-thumbnail-size': '60px',
                '--avatar-thumbnail-border-radius': '30px',
                '--avatar-thumbnail-text-color': '#FFFFFF',
                '--avatar-thumbnail-bg-color': '#0088AB',

                //Product photo or review photo styles:
                '--photo-video-thumbnail-size': '80px',
                '--photo-video-thumbnail-border-radius': '30px',

                //Media (photo & video) slider styles:
                '--mediaslider-scroll-button-icon-color': '#0088AB',
                '--mediaslider-scroll-button-bg-color': '#FFFFFFD9',
                '--mediaslider-overlay-text-color': '#FFFFFF',
                '--mediaslider-overlay-bg-color': '#003542B8',
                '--mediaslider-item-size': '92px',

                //Pagination & tabs styles (normal):
                '--pagination-tab-text-color': '#003542',
                '--pagination-tab-text-transform': 'none',
                '--pagination-tab-text-letter-spacing': '0',
                '--pagination-tab-text-font-size': '18px',
                '--pagination-tab-text-font-weight': '400',

                //Pagination & tabs styles (active):
                '--pagination-tab-active-text-color': '#00A5B4',
                '--pagination-tab-active-text-font-weight': '600',
                '--pagination-tab-active-border-color': '#00A5B4',
                '--pagination-tab-border-width': '1px',

                //AI summary styles
                '--sentiment-base-font-size': '15px',
                '--sentiment-panel-bg-color': 'transparent',
                '--sentiment-panel-border-size': '1px',
                '--sentiment-panel-border-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-panel-border-radius': '0px',
                '--sentiment-panel-shadow-size': '0px',
                '--sentiment-panel-shadow-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-heading-text-color': '#0E1311',
                '--sentiment-heading-text-font-weight': '600',
                '--sentiment-heading-text-font-family': 'inherit',
                '--sentiment-heading-text-line-height': '1.4',
                '--sentiment-heading-text-letter-spacing': '0',
                '--sentiment-heading-text-transform': 'none',
                '--sentiment-body-text-color': '#0E1311',
                '--sentiment-body-text-font-weight': '400',
                '--sentiment-body-text-font-family': 'inherit',
                '--sentiment-body-text-line-height': '1.4',
                '--sentiment-body-text-letter-spacing': '0',
                '--sentiment-body-text-transform': 'none',
                '--sentiment-panel-vertical-padding': '25px',
                '--sentiment-panel-horizontal-padding': '20px',
                '--sentiment-header-text-color': 'inherit',
                '--sentiment-header-text-font-size': '12px',
                '--sentiment-header-text-font-weight': 'inherit',
                '--sentiment-header-bg-color': 'rgba(0, 0, 0, 0.04)',
                '--sentiment-header-border-radius': '50px',
                '--sentiment-header-shadow-size': '0px',
                '--sentiment-header-shadow-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-header-vertical-padding': '7px',
                '--sentiment-header-horizontal-padding': '10px',
                '--sentiment-pagination-tab-text-font-size': '13px',
                '--sentiment-pagination-tab-text-font-weight': '600',
                '--sentiment-pagination-tab-text-color': '#0E1311',
                '--sentiment-pagination-tab-text-transform': 'none',
                '--sentiment-pagination-tab-text-letter-spacing': '0',
                '--sentiment-pagination-tab-active-text-color': '#0E1311',
                '--sentiment-pagination-tab-active-text-font-weight': '600',
                '--sentiment-pagination-tab-active-border-color': '#0E1311',
                '--sentiment-pagination-tab-border-width': '3px',
            },
        };
    }, [store.salesforceValue]);

    useEffect(() => {
        // Skip if we shouldn't render or script isn't loaded yet or already initialized
        if (!renderReviews || !scriptLoaded || widgetInitialized.current) {
            return;
        }

        // Handle script error
        if (scriptError) {
            console.error('Failed to load Reviews.io script');
            return;
        }

        try {
            // Initialize the Reviews.io widget
            new window.ReviewsWidget('#reviews', reviewsConfig);
            widgetInitialized.current = true;

            // Initialize the tab switcher after widget creation
            // This will automatically switch to Product Review tab when it appears
            tabSwitcherCleanupRef.current = initializeReviewTabSwitcher({
                // Now uses defaults: maxRetries: 10, retryDelay: 200
                // Can override if needed:
                // maxRetries: 20,
                // retryDelay: 150,
                debug: false, // Set to true for debugging
            });
        } catch (error) {
            console.error('Error initializing ReviewsWidget:', error);
            widgetInitialized.current = false;
        }

        // Cleanup function
        return () => {
            // Clean up tab switcher
            if (tabSwitcherCleanupRef.current) {
                tabSwitcherCleanupRef.current();
                tabSwitcherCleanupRef.current = null;
            }
            widgetInitialized.current = false;
        };
    }, [renderReviews, scriptLoaded, scriptError, reviewsConfig]);

    if (!renderReviews || scriptError) {
        return null;
    }

    // Show a loading state while script is loading
    if (!scriptLoaded) {
        return (
            <section
                className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 mcl-w-full ${backgroundStyle} ${backgroundGradientStyle} ${topPaddingClasses} ${bottomPaddingClasses}`}
            >
                <div className="container">
                    <div className="mcl-text-center mcl-py-8">
                        <span className="mcl-text-gray-500">Loading reviews...</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="reviews-section"
            className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 mcl-w-full ${backgroundStyle} ${backgroundGradientStyle} ${topPaddingClasses} ${bottomPaddingClasses}`}
        >
            <Typography className="mcl-text-center mcl-mt-0 mcl-mb-12" color="Blue" tag="h2" variant="h2-large">
                {title}
            </Typography>
            <div className="container">
                <div id="reviews" />
            </div>
        </section>
    );
}
